/**
 * Tasks command handlers
 */

import { createTaskService, createAttachmentService, getApiUrl } from '../utils/client.js';
import { buildAttachmentFileParts } from '../utils/attach-files.js';
import { resolveLabelsForListFilter, resolveLabelsToIds } from './projects.js';
import { searchTasks } from './search.js';
import { chalk, formatOutput, formatTaskTable, formatSuccess, formatPagination, formatInfo, makeLink, isInteractiveOutput } from '../utils/format.js';
import { buildTaskLinkFromTicketWithBase } from '../../../../src/utils/task-link.js';
import { cliDescriptionToHtml, htmlToPlainText } from '../../../../src/utils/cli-rich-text.js';
import type { AttachFilesResponse } from '../../../../src/lib/services/attachment.service.js';
import type { TaskTreeNode } from '../../../../src/lib/services/task.service.js';
import { buildAttachFailureMessage, handleApiError, requireOption, parseNumber, parseList, cliError } from '../utils/errors.js';

export const TASK_LIST_SEARCH_HELP =
  'Search tasks by keyword instead of listing. When set, only --project scoping is applied; other list filters are ignored.';

export async function listTasks(
  token?: string,
  apiUrl?: string,
  options?: {
    search?: string;
    project?: number;
    section?: string;
    assignedTo?: string;
    /** Creator user id (server-side filter; HTPR-3088) */
    createdBy?: number;
    priority?: string;
    /** Comma-separated label names or IDs (for server-side filter) */
    labels?: string;
    sortBy?: "createdAt" | "updatedAt" | "dueDate" | "priority" | "title";
    sortOrder?: "asc" | "desc"
    limit?: number;
    offset?: number;
    status?: string;
    json?: boolean;
  }
): Promise<void> {
  try {
    if (options?.search != null) {
      await searchTasks(options.search, token, apiUrl, {
        project: options.project,
        limit: options.limit,
        json: options.json,
      });
      return;
    }

    const service = await createTaskService(token, apiUrl);

    const params: any = {
      project_id: options?.project,
      assigned_to: options?.assignedTo,
      limit: options?.limit || 10,
      offset: options?.offset || 0,
    };

    if (options?.section) {
      const sectionVal = options.section.trim();
      if (options?.project == null) {
        throw new Error('--project is required when using --section');
      }
      const { createSectionService } = await import('../utils/client.js');
      const sectionService = await createSectionService(token, apiUrl);
      const sectionsResult = await sectionService.manageSection({
        action: 'list',
        project_id: options.project,
      });
      const sections = (sectionsResult as any).sections || [];
      const sectionIdNum = parseInt(sectionVal, 10);
      const target =
        !isNaN(sectionIdNum) && String(sectionIdNum) === sectionVal
          ? sections.find((s: any) => s.id === sectionIdNum)
          : sections.find(
              (s: any) => s.section_title.toLowerCase() === sectionVal.toLowerCase()
            );
      if (!target) {
        throw new Error(
          `Section "${sectionVal}" not found. Available Sections: ${sections.map((s: any) => `${s.section_title} (${s.id})`).join(', ')}`,
        );
      }
      // Production GET /mcp/tasks filters by `section` (column title). Sending only section_id
      // is ignored by some backends; always send the canonical title from the sections API.
      params.section = target.section_title;
    }

    if (options?.createdBy != null) {
      params.created_by = options.createdBy;
    }

    if (options?.priority) {
      params.priority = parseList(options.priority);
    }

    if (options?.sortBy) {
      const validSortBy = ["createdAt", "updatedAt", "dueDate", "priority", "title"];
      if (!validSortBy.includes(options.sortBy)) {
        throw new Error(`--sort-by must be one of: ${validSortBy.join(", ")}`);
      }
      params.sort_by = options.sortBy;
    }

    if (options?.sortOrder) {
      if (options.sortOrder !== "asc" && options.sortOrder !== "desc") {
        throw new Error('--sort-order must be "asc" or "desc"');
      }
      params.sort_order = options.sortOrder;
    }

    if (options?.status) {
      params.status = options.status;
    }

    if (options?.labels) {
      params.labels = await resolveLabelsForListFilter(
        options.labels,
        options.project,
        token,
        apiUrl
      );
    }

    const result = await service.listTasks(params);

    if (options?.json) {
      console.log(formatOutput(result, true));
    } else {
      const tasks = result?.tasks ?? [];
      if (tasks.length === 0) {
        console.log('No tasks found');
        return;
      }

      const total = result?.total ?? tasks.length;
      const appBase = new URL(getApiUrl(apiUrl)).origin;
      if (isInteractiveOutput()) {
        console.log(`\nTasks (${total} total):`);
      }
      console.log(formatTaskTable(tasks, appBase));

      if (result?.has_more && isInteractiveOutput()) {
        const offset = result?.offset ?? 0;
        const limit = result?.limit ?? 10;
        console.log('');
        console.log(formatPagination(offset, limit, total, true));
      }
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function getTask(
  ticket: string,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    requireOption(ticket, 'ticket');
    const service = await createTaskService(token, apiUrl);
    const result = await service.getTask({ ticket_number: [ticket] });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      const task = result.tasks?.[0] || result.task || result;
      if (!task) {
        throw new Error('Task not found');
      }
      console.log('');
      console.log(chalk.bold.cyan(`  ${task.ticketNumber}`) + '  ' + chalk.bold.white(task.title));
      console.log(chalk.dim('  ' + '─'.repeat(50)));

      console.log(`  ${chalk.dim('Section')}    ${chalk.white(task.section)}  ${chalk.dim(`(${task.boardTitle})`)}`);

      const priority = task.priority?.Priority_Value || 'None';
      const priorityColor = priority === 'Urgent' ? chalk.red : priority === 'High' ? chalk.yellow : priority === 'Medium' ? chalk.blue : chalk.dim;
      console.log(`  ${chalk.dim('Priority')}  ${priorityColor(priority)}`);

      if (task.assignees && task.assignees.length > 0) {
        console.log(`  ${chalk.dim('Assignees')} ${task.assignees.map((a: any) => chalk.white(a.displayName)).join(chalk.dim(', '))}`);
      }
      if (task.labels && task.labels.length > 0) {
        console.log(`  ${chalk.dim('Labels')}    ${task.labels.map((l: any) => chalk.magenta(l.name)).join(chalk.dim(', '))}`);
      }
      if (task.dueDate) {
        console.log(`  ${chalk.dim('Due')}       ${chalk.yellow(task.dueDate)}`);
      }
      console.log(`  ${chalk.dim('Comments')}  ${chalk.dim(String(task.totalComments || 0))}`);
      console.log(`  ${chalk.dim('Status')}    ${chalk.white(task.status)}`);


      if (task.description) {
        const plain = htmlToPlainText(task.description);
        if (plain) {
          console.log('');
          console.log(chalk.dim('  DESCRIPTION'));
          plain.split('\n').forEach((line) => console.log(`  ${chalk.white(line)}`));
        }
      }

      const appBase = new URL(getApiUrl(apiUrl)).origin;
      const taskUrl = buildTaskLinkFromTicketWithBase(appBase, task.ticketNumber ?? '', task.projectId);
      if (taskUrl) {
        console.log('');
        console.log(`  ${chalk.dim(makeLink(taskUrl, taskUrl))}`);
      }
      console.log('');
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function createTask(
  token?: string,
  apiUrl?: string,
  options?: {
    project: number;
    title: string;
    description?: string;
    priority?: string;
    estimate?: number;
    labels?: string;
    due?: string;
    /** Local file paths to upload after task is created */
    attach?: string[];
    parentTaskId?: number;
    section?: string;
    assignee?: number[];
    json?: boolean;
  }
): Promise<void> {
  try {
    requireOption(options?.project, 'project');
    requireOption(options?.title, 'title');

    const service = await createTaskService(token, apiUrl);
    const params: any = {
      project_id: options!.project,
      title: options!.title,
      due_date: options?.due,
    };
    if (options?.description) {
      params.description = cliDescriptionToHtml(options.description);
    }

    if (options?.priority) {
      const priorityMap: any = { urgent: 1, high: 2, medium: 3, low: 4, none: 0 };
      params.priority = priorityMap[options.priority.toLowerCase()] ?? 0;
    }

    if (options?.estimate) {
      params.estimate = options.estimate;
    }

    if (options?.labels) {
      params.labels = await resolveLabelsToIds(
        options.labels,
        options!.project,
        token,
        apiUrl
      );
    }

    if (options?.parentTaskId) {
      requireOption(options.parentTaskId, 'parent-task');
      params.parent_task_id = options.parentTaskId;
    }

    if (options?.assignee) {
      if (!Array.isArray(options.assignee) || !options.assignee.every(id => Number.isInteger(id))) {
        cliError('assignee must be a list of integer user IDs', 2);
      }
      params.assignee = options.assignee;
    }

    if (options?.section) {
      const sectionVal = options.section.trim();
      const sectionIdNum = parseInt(sectionVal, 10);
      if (!isNaN(sectionIdNum) && String(sectionIdNum) === sectionVal) {
        params.section_id = sectionIdNum;
      } else {
        const { createSectionService } = await import('../utils/client.js');
        const sectionService = await createSectionService(token, apiUrl);
        const projectId = options.project;
        const sectionsResult = await sectionService.manageSection({
          action: 'list',
          project_id: projectId,
        });
        const sections = (sectionsResult as any).sections || [];
        const target = sections.find((s: any) => s.section_title.toLowerCase() === sectionVal.toLowerCase());
        if (!target) {
          throw new Error(`Section "${sectionVal}" not found. Available: ${sections.map((s: any) => s.section_title).join(', ')}`);
        }
        params.section_id = target.id;
      }
    }


    const result = await service.createTask(params);

    let attachResult: AttachFilesResponse | null = null;
    if (options?.attach?.length) {
      let ticket = '';
      let taskId: number | undefined;
      try {
        const task = result.task || result;
        ticket = getDisplayTicketNumber(task);
        taskId =
          task.id != null && Number.isFinite(Number(task.id))
            ? Number(task.id)
            : undefined;
        const attachmentService = await createAttachmentService(token, apiUrl);
        const files = await buildAttachmentFileParts(options.attach);
        const ident =
          ticket !== ''
            ? { ticket_number: ticket }
            : taskId != null
              ? { task_id: taskId }
              : null;
        if (!ident) {
          throw new Error('Cannot attach files: task has no ticket number or id in API response');
        }
        attachResult = await attachmentService.attachFiles({ ...ident, files });
      } catch (error) {
        cliError(buildAttachFailureMessage(ticket, taskId, error), 5);
      }
    }

    if (options?.json) {
      const out =
        attachResult && options?.attach?.length
          ? { ...result, attachments_uploaded: attachResult }
          : result;
      console.log(formatOutput(out, true));
    } else {
      const task = result.task || result;
      const ticketNumber = getDisplayTicketNumber(task);

      console.log('');
      console.log(`  ${chalk.green.bold('✓ Task created')}`);
      console.log(chalk.dim('  ' + '─'.repeat(40)));
      if (ticketNumber) {
        console.log(`  ${chalk.dim('Ticket')}   ${chalk.bold.cyan(ticketNumber)}`);
      }
      console.log(`  ${chalk.dim('Title')}    ${chalk.bold.white(task.title)}`);

      const appBase = new URL(getApiUrl(apiUrl)).origin;
      const taskUrl = ticketNumber
        ? buildTaskLinkFromTicketWithBase(appBase, ticketNumber, task.projectId)
        : null;
      if (taskUrl) {
        console.log(`  ${chalk.dim('Link')}     ${chalk.dim(taskUrl)}`);
      }

      if (attachResult?.attachments?.length) {
        console.log('');
        console.log(chalk.dim('  ATTACHMENTS'));
        for (const a of attachResult.attachments) {
          const urlPart = a.url ? `  ${chalk.dim(a.url)}` : '';
          console.log(`  ${chalk.green('↑')}  ${chalk.white(a.fileName)}${urlPart}`);
        }
      }
      console.log('');
    }
  } catch (error) {
    handleApiError(error);
  }
}

/**
 * Get display ticket number for create/update output (HTPR-3034).
 * Never returns internal task.id. Prefers ticketNumber if valid (PREFIX-NUMBER format),
 * otherwise derives from uniqueIndex + projectId.
 */
function getDisplayTicketNumber(task: {
  ticketNumber?: string | number;
  projectId?: number;
  uniqueIndex?: number;
  unique_index?: number;
  id?: number;
}): string {
  const tn = task.ticketNumber;
  // Valid ticket format: "PREFIX-NUMBER" (e.g. HTPR-105, PROJ-789). Reject raw numbers (internal id).
  const tnStr = typeof tn === 'string' ? tn.trim() : '';
  if (tnStr && tnStr.includes('-') && /^[A-Z0-9]+-\d+$/i.test(tnStr)) {
    return tnStr;
  }
  const uniqueIndex = task.uniqueIndex ?? (task as any).unique_index;
  const projectId = task.projectId;
  if (typeof uniqueIndex === 'number' && typeof projectId === 'number') {
    return `P${projectId}-${uniqueIndex}`;
  }
  return '';
}

export function validateTaskUpdateClearOptions(options: {
  due?: string;
  clearDue?: boolean;
  parentTask?: unknown;
  clearParent?: boolean;
}): void {
  if (options.due !== undefined && options.clearDue) {
    cliError("Cannot use --due and --clear-due together");
  }
  if (options.parentTask !== undefined && options.clearParent) {
    cliError("Cannot use --parent-task and --clear-parent together");
  }
}

export async function updateTask(
  ticket: string,
  token?: string,
  apiUrl?: string,
  options?: {
    title?: string;
    description?: string;
    priority?: string;
    estimate?: number;
    due?: string;
    clearDue?: boolean;
    status?: string;
    section?: string;
    project?: number;
    labels?: string;
    attach?: string[];
    assignee?: number[];
    parentTaskId?: number;
    clearParent?: boolean;
    json?: boolean;
  }
): Promise<void> {
  try {
    requireOption(ticket, 'ticket');
    const service = await createTaskService(token, apiUrl);

    const params: any = { ticket_number: ticket };

    if (options?.title) params.title = options.title;
    if (options?.description) params.description = cliDescriptionToHtml(options.description);
    if (options?.clearDue) {
      params.due_date = null;
    } else if (options?.due) {
      params.due_date = options.due;
    }
    if (options?.status) params.status = options.status;
    if (options?.priority) {
      const priorityMap: any = { urgent: 1, high: 2, medium: 3, low: 4, none: 0 };
      params.priority = priorityMap[options.priority.toLowerCase()] ?? 0;
    }

    if (options?.estimate) {
      params.estimate = options.estimate;
    }

    if (options?.section) {
      const sectionVal = options.section.trim();
      const sectionIdNum = parseInt(sectionVal, 10);
      if (!isNaN(sectionIdNum) && String(sectionIdNum) === sectionVal) {
        params.sectionId = sectionIdNum;
      } else {
        const { createSectionService } = await import('../utils/client.js');
        const sectionService = await createSectionService(token, apiUrl);
        const taskResult = await service.getTask({ ticket_number: [ticket] });
        const task = taskResult.tasks?.[0];
        if (!task) throw new Error(`Task ${ticket} not found`);
        const projectId = options.project ?? task.projectId;
        const sectionsResult = await sectionService.manageSection({
          action: 'list',
          project_id: projectId,
        });
        const sections = (sectionsResult as any).sections || [];
        const target = sections.find((s: any) => s.section_title.toLowerCase() === sectionVal.toLowerCase());
        if (!target) {
          throw new Error(`Section "${sectionVal}" not found. Available: ${sections.map((s: any) => s.section_title).join(', ')}`);
        }
        params.sectionId = target.id;
      }
    }

    if (options?.assignee) {
      if (!Array.isArray(options.assignee) || !options.assignee.every(id => Number.isInteger(id))) {
        cliError('assignee must be a list of integer user IDs', 2);
      }
      params.assignee = options.assignee;
    }

    if (options?.clearParent) {
      params.parent_task_id = null;
    } else if (options?.parentTaskId) {
      requireOption(options.parentTaskId, 'parent-task');
      params.parent_task_id = options.parentTaskId;
    }

    if (options?.labels) {
      const taskResult = await service.getTask({ ticket_number: [ticket] });
      const task = taskResult.tasks?.[0];
      if (!task) throw new Error(`Task ${ticket} not found`);
      params.labels = await resolveLabelsToIds(
        options.labels,
        task.projectId,
        token,
        apiUrl
      );
    }

    const hasUpdateFields =
      params.title !== undefined ||
      params.description !== undefined ||
      params.due_date !== undefined ||
      params.status !== undefined ||
      params.priority !== undefined ||
      params.estimate !== undefined ||
      params.sectionId !== undefined ||
      params.assignee !== undefined ||
      params.labels !== undefined ||
      params.parent_task_id !== undefined;

    let result: Awaited<ReturnType<typeof service.updateTask>> | Awaited<
      ReturnType<typeof service.getTask>
    > | null = null;
    let attachUpdates: AttachFilesResponse | null = null;

    if (!hasUpdateFields && options?.attach?.length) {
      result = await service.getTask({ ticket_number: [ticket] });
      const taskRow = result.tasks?.[0];
      if (!taskRow) throw new Error(`Task ${ticket} not found`);
      const attachmentService = await createAttachmentService(token, apiUrl);
      const files = await buildAttachmentFileParts(options.attach);
      attachUpdates = await attachmentService.attachFiles({ ticket_number: ticket, files });
    } else {
      result = await service.updateTask(params);
      if (options?.attach?.length) {
        const attachmentService = await createAttachmentService(token, apiUrl);
        const files = await buildAttachmentFileParts(options.attach);
        attachUpdates = await attachmentService.attachFiles({ ticket_number: ticket, files });
      }
    }

    if (options?.json) {
      const base =
        result && 'task' in result && result.task
          ? result
          : result && 'tasks' in result && result.tasks?.[0]
            ? { success: true, task: result.tasks[0] }
            : result;
      const out =
        attachUpdates && options?.attach?.length && base && typeof base === 'object'
          ? { ...base, attachments_uploaded: attachUpdates }
          : base;
      console.log(formatOutput(out, true));
    } else {
      const didAttachOnly = !hasUpdateFields && !!options?.attach?.length;
      const task: any =
        result && 'task' in result && result.task
          ? result.task
          : result && 'tasks' in result
            ? result.tasks?.[0]
            : result;

      console.log('');
      console.log(`  ${chalk.green.bold(didAttachOnly ? '✓ Files attached' : '✓ Task updated')}`);
      console.log(chalk.dim('  ' + '─'.repeat(40)));
      console.log(`  ${chalk.dim('Ticket')}   ${chalk.bold.cyan(ticket)}`);
      if (task?.title) {
        console.log(`  ${chalk.dim('Title')}    ${chalk.bold.white(task.title)}`);
      }

      const appBase = new URL(getApiUrl(apiUrl)).origin;
      const taskUrl = buildTaskLinkFromTicketWithBase(appBase, task?.ticketNumber ?? ticket, task?.projectId);
      if (taskUrl) {
        console.log(`  ${chalk.dim('Link')}     ${chalk.dim(makeLink(taskUrl, taskUrl))}`);
      }

      if (attachUpdates?.attachments?.length) {
        console.log('');
        console.log(chalk.dim('  ATTACHMENTS'));
        for (const a of attachUpdates.attachments) {
          const urlPart = a.url ? `  ${chalk.dim(a.url)}` : '';
          console.log(`  ${chalk.green('↑')}  ${chalk.white(a.fileName)}${urlPart}`);
        }
      }
      console.log('');
    }
  } catch (error) {
    handleApiError(error);
  }
}

async function setTaskAssignee(
  ticket: string,
  userId: number,
  token: string | undefined,
  apiUrl: string | undefined,
  json: boolean | undefined,
  intent: 'assign' | 'unassign'
): Promise<void> {
  requireOption(ticket, 'ticket');
  requireOption(userId.toString(), 'user');

  const service = await createTaskService(token, apiUrl);
  const result = await service.assignUser({
    ticket_number: ticket,
    user_id: userId,
    intent,
  });

  if (json) {
    console.log(formatOutput(result, true));
  } else if (intent === 'unassign') {
    console.log(formatSuccess(`Unassigned user ${userId} from task ${ticket}`));
  } else {
    console.log(formatSuccess(`Assigned user ${userId} to task ${ticket}`));
  }
}

export async function assignTask(
  ticket: string,
  userId: number,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    await setTaskAssignee(ticket, userId, token, apiUrl, json, 'assign');
  } catch (error) {
    handleApiError(error);
  }
}

export async function unassignTask(
  ticket: string,
  userId: number,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    await setTaskAssignee(ticket, userId, token, apiUrl, json, 'unassign');
  } catch (error) {
    handleApiError(error);
  }
}

export async function moveTask(
  ticket: string,
  sectionName: string,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    requireOption(ticket, 'ticket');
    requireOption(sectionName, 'section');

    const taskService = await createTaskService(token, apiUrl);
    
    // Lazy import section service
    const { createSectionService } = await import('../utils/client.js');
    const sectionService = await createSectionService(token, apiUrl);

    // 1. Get task to find its project_id
    const taskResult = await taskService.getTask({ ticket_number: [ticket] });
    if (!taskResult.tasks || taskResult.tasks.length === 0) {
      throw new Error(`Task ${ticket} not found`);
    }
    const task = taskResult.tasks[0];
    const projectId = task.projectId;

    // 2. Get sections for the project
    const sectionsResult = await sectionService.manageSection({
      action: 'list',
      project_id: projectId,
    });
    
    const sectionsResponse = sectionsResult as any;
    const sections = sectionsResponse.sections || [];

    // 3. Find the matching section
    const targetSection = sections.find((s: any) => s.section_title.toLowerCase() === sectionName.toLowerCase());
    
    if (!targetSection) {
      throw new Error(`Section "${sectionName}" not found. Available sections: ${sections.map((s: any) => s.section_title).join(', ')}`);
    }

    // 4. Update the task with the new sectionId
    const result = await taskService.updateTask({
      ticket_number: ticket,
      sectionId: targetSection.id,
    });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log(formatSuccess(`Moved task ${ticket} to section "${targetSection.section_title}"`));
    }
  } catch (error) {
    handleApiError(error);
  }
}

export async function moveTaskToBoard(
  taskId: number,
  targetProjectId: number,
  targetSectionId?: number,
  token?: string,
  apiUrl?: string,
  json?: boolean
): Promise<void> {
  try {
    if (!Number.isInteger(taskId) || taskId < 1) {
      cliError('task id must be a positive integer', 2);
    }

    requireOption(targetProjectId, 'target project');

    const service = await createTaskService(token, apiUrl);
    const result = await service.moveTaskBetweenBoards({
      task_id: taskId,
      target_project_id: targetProjectId,
      target_section_id: targetSectionId,
    });

    if (json) {
      console.log(formatOutput(result, true));
    } else {
      console.log(formatSuccess(`Moved task ${taskId} to project ${targetProjectId}`));
      const task = result.task || result;
      const label = task.ticketNumber?.trim() || `#${taskId}`;
      console.log(formatSuccess(`Moved task ${label} (id ${taskId}) to project ${targetProjectId}`));
      const appBase = new URL(getApiUrl(apiUrl)).origin;
      const taskUrl = buildTaskLinkFromTicketWithBase(
        appBase,
        task.ticketNumber ?? String(taskId),
        targetProjectId
      );
      if (taskUrl) console.log(formatInfo(taskUrl));
    }
  } catch (error) {
    handleApiError(error);
  }
}

export interface GetTaskTreeCliOptions {
  ticket?: string;
  taskId?: number;
  depth?: number;
  json?: boolean;
}

function formatTaskTreeNodeLabel(
  node: TaskTreeNode,
  isRoot: boolean,
  highlight?: { ticket?: string; taskId?: number }
): string {
  const id = node.ticketNumber?.trim() || `#${node.id}`;
  const ticketMatch =
    highlight?.ticket !== undefined &&
    node.ticketNumber !== undefined &&
    node.ticketNumber.trim().toLowerCase() === highlight.ticket.trim().toLowerCase();
  const idMatch = highlight?.taskId !== undefined && node.id === highlight.taskId;
  const isHighlighted = ticketMatch || idMatch;

  const ticketPart = isHighlighted
    ? chalk.bold.cyan(id)
    : isRoot
      ? chalk.bold.white(id)
      : chalk.cyan(id);

  const titlePart = isHighlighted
    ? chalk.bold.white(node.title)
    : isRoot
      ? chalk.bold.white(node.title)
      : chalk.white(node.title);

  const marker = isHighlighted ? `  ${chalk.yellow('← you are here')}` : '';

  return `${ticketPart}  ${titlePart}${marker}`;
}

function printTaskTreeNode(
  node: TaskTreeNode,
  prefix: string,
  isLast: boolean,
  isRoot: boolean,
  level: number,
  maxLevel: number | undefined,
  highlight?: { ticket?: string; taskId?: number }
): void {
  const connector = isRoot ? '' : isLast ? chalk.dim('└── ') : chalk.dim('├── ');
  console.log(prefix + connector + formatTaskTreeNodeLabel(node, isRoot, highlight));

  if (maxLevel !== undefined && level >= maxLevel) {
    return;
  }

  const children = node.children ?? [];
  const extension = isRoot ? '' : isLast ? '    ' : chalk.dim('│   ');
  const childPrefix = prefix + extension;

  children.forEach((child, i) => {
    printTaskTreeNode(
      child,
      childPrefix,
      i === children.length - 1,
      false,
      level + 1,
      maxLevel,
      highlight
    );
  });
}

export async function getTaskTree(
  token?: string,
  apiUrl?: string,
  options?: GetTaskTreeCliOptions
): Promise<void> {
  try {
    const ticket = options?.ticket?.trim();
    const taskId = options?.taskId;
    const hasTicket = ticket !== undefined && ticket.length > 0;
    const hasTaskId = taskId !== undefined && Number.isFinite(taskId) && taskId > 0;

    if (!hasTicket && !hasTaskId) {
      throw new Error('Provide either --ticket <ticket> or --task-id <id>');
    }
    if (hasTicket && hasTaskId) {
      throw new Error('Provide only one of --ticket or --task-id, not both');
    }

    const service = await createTaskService(token, apiUrl);
    const params: {
      ticket_number?: string;
      task_id?: number;
      depth?: number;
    } = {};
    if (hasTicket) {
      params.ticket_number = ticket;
    } else {
      params.task_id = taskId as number;
    }
    if (options?.depth !== undefined) {
      if (!Number.isFinite(options.depth) || options.depth < 1) {
        throw new Error('--depth must be a positive integer');
      }
      params.depth = Math.floor(options.depth);
    }

    const result = await service.getTaskTree(params);

    if (options?.json) {
      console.log(formatOutput(result, true));
      return;
    }

    if (!result.tree) {
      console.log('No tree data returned');
      return;
    }

    const highlight =
      hasTicket && ticket
        ? { ticket }
        : hasTaskId
          ? { taskId: taskId as number }
          : undefined;

    console.log('');
    console.log(chalk.bold.white('  Task Tree'));
    console.log(chalk.dim('  ' + '─'.repeat(40)));
    console.log('');
    printTaskTreeNode(result.tree, '  ', true, true, 1, params.depth, highlight);
    console.log('');
  } catch (error) {
    handleApiError(error);
  }
}
